import type { I18n } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';

import { action, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import { hireNext, vehicleBorrowerFill, vehicleFill, vehicleProposalFill } from './simulation-hire.fill';
import { summaryContextText, type SummaryAgentState } from './simulation-summary.context';
import { SIMULATION_READ, VEHICLE_PATH } from './simulation.screen';

// Manifestos de Veículo (U23, U42): o hub e as duas jornadas (router.tsx:1085-1123). Arquivo leve, entra no
// `src/app/screens.ts`. Herdam `CreditNote:Read` de `/ccb` e só existem com `ENABLE_NEW_SIMULATION === 1`.

export const CAR_EQUITY_PATH = `${VEHICLE_PATH}/car-equity`;
export const VEHICLE_FINANCING_PATH = `${VEHICLE_PATH}/financiamento`;

const NEW_SIMULATION = { flag: { key: 'ENABLE_NEW_SIMULATION', equals: 1 } } as const;

export const simulationVehicleCarEquity = action({
  id: 'simulation.vehicle.carEquity',
  kind: 'navigate',
  label: msg`Car Equity`,
  description: msg`Abre a simulação de Car Equity: crédito com o veículo quitado do cliente como garantia.`,
  permission: SIMULATION_READ,
  run: ({ nav }) => nav.to(CAR_EQUITY_PATH),
});

export const simulationVehicleFinancing = action({
  id: 'simulation.vehicle.financing',
  kind: 'navigate',
  label: msg`Financiamento de Veículo`,
  description: msg`Abre a simulação de Financiamento de Veículo: crédito para comprar um veículo novo ou usado.`,
  permission: SIMULATION_READ,
  run: ({ nav }) => nav.to(VEHICLE_FINANCING_PATH),
});

export const simulationVehicleScreen = defineScreen({
  id: 'simulation.vehicle',
  path: VEHICLE_PATH,
  title: msg`Veículo`,
  description: msg`Escolha do tipo de operação com garantia veicular: Car Equity ou Financiamento de Veículo.`,
  permission: SIMULATION_READ,
  access: NEW_SIMULATION,
  place: { icon: 'file-text', parent: 'simulation.hire', flow: true },
  actions: [simulationVehicleCarEquity, simulationVehicleFinancing],
});

/** Nenhum alvo: as ferramentas agem sobre o fluxo aberto (trava Lite, agent-tool-targets). */
export const vehicleOffersInput = z.strictObject({});
/** O valor da proposta, em centavos; o alvo é o fluxo aberto. */
export const vehicleEditProposalInput = z.strictObject({
  valueInCents: z.number().int().positive(),
});

const mountedScreenRequired = (): never => {
  throw new Error(t`Abra a simulação de veículo para usar esta ação.`);
};

function flowActions(prefix: 'carEquity' | 'vehicleFinancing') {
  return {
    offers: action({
      id: `simulation.${prefix}.offers`,
      kind: 'write',
      label: msg`Simular ofertas`,
      description: msg`Com a pessoa e o veículo preenchidos, simula as ofertas de parcelamento e abre a escolha da proposta. Não cria operação. Pede a confirmação da pessoa.`,
      keywords: [msg`ofertas`, msg`simular`, msg`parcelas`],
      permission: SIMULATION_READ,
      input: vehicleOffersInput,
      preview: () => ({
        title: t`Simular as ofertas?`,
        detail: t`Simula as ofertas com os dados do veículo. Não cria operação.`,
      }),
      run: mountedScreenRequired,
    }),
    editProposal: action({
      id: `simulation.${prefix}.editProposal`,
      kind: 'write',
      label: msg`Editar proposta`,
      description: msg`Refaz a simulação das ofertas com um valor informado, dentro do limite disponível para contratação. Não cria operação. Pede a confirmação da pessoa.`,
      keywords: [msg`proposta`, msg`valor`],
      permission: SIMULATION_READ,
      input: vehicleEditProposalInput,
      preview: () => ({
        title: t`Refazer as ofertas?`,
        detail: t`Simula de novo as ofertas com o valor informado. Não cria operação.`,
      }),
      run: mountedScreenRequired,
    }),
  };
}

export const carEquityActions = flowActions('carEquity');
export const vehicleFinancingActions = flowActions('vehicleFinancing');

/** O Luy no veículo (U56): tomador, veículo, a proposta no Editar proposta e avançar, sem simular nem buscar a placa. */
function vehicleFills(prefix: 'carEquity' | 'vehicleFinancing') {
  const id = `simulation.${prefix}`;
  return {
    borrower: vehicleBorrowerFill(id),
    vehicle: vehicleFill(id, prefix === 'vehicleFinancing'),
    proposal: vehicleProposalFill(id),
    next: hireNext(id),
  };
}
export const carEquityFills = vehicleFills('carEquity');
export const vehicleFinancingFills = vehicleFills('vehicleFinancing');

export const simulationCarEquityScreen = defineScreen({
  id: 'simulation.carEquity',
  path: CAR_EQUITY_PATH,
  title: msg`Car Equity`,
  description: msg`Simulação de Car Equity em passos: o CPF da pessoa, os dados do veículo (com a busca pela placa e a FIPE) e a escolha da proposta de parcelamento. No Resumo, a conferência do titular e da conta e o envio para análise, que cria a operação.`,
  permission: SIMULATION_READ,
  access: NEW_SIMULATION,
  place: { icon: 'file-text', parent: 'simulation.vehicle', flow: true },
  actions: [carEquityActions.offers, carEquityActions.editProposal, ...Object.values(carEquityFills)],
});

export const simulationVehicleFinancingScreen = defineScreen({
  id: 'simulation.vehicleFinancing',
  path: VEHICLE_FINANCING_PATH,
  title: msg`Financiamento de Veículo`,
  description: msg`Simulação de Financiamento de Veículo em passos: o CPF da pessoa, a entrada, os dados do veículo (com a busca pela placa e a FIPE) e a escolha da proposta de parcelamento. No Resumo, a conferência do titular e da conta, o beneficiário e a conta de liquidação e o envio para análise, que cria a operação.`,
  permission: SIMULATION_READ,
  access: NEW_SIMULATION,
  place: { icon: 'file-text', parent: 'simulation.vehicle', flow: true },
  actions: [vehicleFinancingActions.offers, vehicleFinancingActions.editProposal, ...Object.values(vehicleFinancingFills)],
});

/** Contexto do hub de Veículo. */
/** Contexto do hub de Veículo (U46): a etapa à vista, o produto já escolhido e a operação marcada. */
export function simulationVehicleContext(i18n: I18n, chosen?: string | null): ScreenContext {
  const modality = chosen ?? '';
  return {
    summary: [
      i18n._(msg`Contratar veículo, etapa 2: Modalidade. O produto já foi escolhido.`),
      i18n._(msg`Escolha entre Car Equity e Financiamento de Veículo. As duas simulações abrem aqui.`),
      modality ? i18n._(msg`Modalidade marcada: ${modality}.`) : '',
    ]
      .filter(Boolean)
      .join(' '),
    fields: { step: 'modalidade', modalityChosen: modality !== '' },
    sensitivity: 'internal',
  };
}

export interface VehicleFlowContextInput {
  readonly financing: boolean;
  readonly step: string;
  readonly stepTitle: string;
  readonly personFound: boolean;
  readonly offers: number | null;
  readonly restrictions: number;
  readonly selected: boolean;
  /** Só no Resumo: se a pessoa e a conta estão prontas para o envio. */
  readonly summary?: SummaryAgentState | null;
}

/** O que o agente lê do fluxo (U24, U46): o passo, se há pessoa e quantas ofertas. Sem CPF, placa nem valores. */
export function vehicleFlowContext(input: VehicleFlowContextInput, i18n: I18n): ScreenContext {
  const kind = input.financing
    ? i18n._(msg`Simulação de Financiamento de Veículo.`)
    : i18n._(msg`Simulação de Car Equity.`);
  const step = input.stepTitle;
  const person = input.personFound ? i18n._(msg`A pessoa do CPF está cadastrada.`) : '';
  const count = input.offers ?? 0;
  const offers =
    input.offers === null
      ? ''
      : count > 0
        ? i18n._(msg`Há ${count} ofertas para escolher.`)
        : input.restrictions > 0
          ? i18n._(msg`Não há ofertas: foram encontradas restrições.`)
          : i18n._(msg`Não há ofertas.`);
  const selected = input.selected ? i18n._(msg`Uma proposta está escolhida.`) : '';
  return {
    summary: [kind, i18n._(msg`Passo atual: ${step}.`), person, offers, selected, summaryContextText(input.summary, i18n)]
      .filter(Boolean)
      .join(' '),
    fields: {
      step: input.step,
      personFound: input.personFound,
      offers: input.offers,
      restrictions: input.restrictions,
      selected: input.selected,
      ...(input.summary ? { bankAccount: input.summary.bankAccount } : {}),
    },
    sensitivity: 'internal',
  };
}
